import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  selectCurrentSong,
  selectEffectsOrder,
  selectEffectsState,
  selectInitialSong,
} from 'services/store/remixer/selectors';
import { Effects } from 'services/store/remixer/types';
import { EFFECT_VALUE_DIVISOR } from 'pages/Remixer/constants';
import {
  createOfflineAudioContext,
  createAudioBufferNode,
  loadBuffer,
  toMP3,
} from '../audioHelpers';
import useCreateEffectsChain from './useCreateEffectsChain';

const DURATION_DIVISOR = 1000;

/**
 * due to clipping caused by effects
 * we need to reduce the amplitude by -1dB
 */
const GAIN_REDUCTION_VALUE = 0.891;

const useRenderSong = () => {
  const currentSong = useSelector(selectCurrentSong);
  const currentSongData = currentSong?.data ?? null;

  const initialSong = useSelector(selectInitialSong);
  const initialSongData = initialSong?.data ?? null;

  const { musicFilePath, duration } = currentSongData || {};

  const effectsState = useSelector(selectEffectsState);

  const effectsOrder = useSelector(selectEffectsOrder);

  const [isRendering, setIsRendering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isReady = useMemo(
    () => !!initialSongData && !!currentSongData,
    [initialSongData, currentSongData]
  );

  const { create: createEffectsChain } = useCreateEffectsChain();

  const render = async () => {
    const totalTimeStart = dayjs();

    setIsRendering(true);

    const isSongDownloadable = isReady && duration && musicFilePath;
    if (!isSongDownloadable) {
      throw new Error('could not render current song');
    }

    const offlineAudioContext = createOfflineAudioContext(
      duration / DURATION_DIVISOR
    );

    const loadBufferTimeStart = dayjs();
    const songBuffer = await loadBuffer(musicFilePath, offlineAudioContext);
    const loadBufferTime = dayjs().diff(loadBufferTimeStart);

    const source = createAudioBufferNode(offlineAudioContext, songBuffer);

    setIsLoading(true);
    const createEffectsChainTimeStart = dayjs();
    const filteredEffectsOrder = effectsOrder.filter(
      (effectName) => effectsState[effectName] > 0
    );

    const chain = await createEffectsChain(
      filteredEffectsOrder,
      offlineAudioContext
    );
    setIsLoading(false);

    Object.keys(chain.nodes).forEach((effectName) => {
      const node = chain.nodes[effectName as Effects];
      if (!node) {
        return;
      }
      const value = effectsState[effectName as Effects];
      node.control.value = value / EFFECT_VALUE_DIVISOR;
    });

    source.connect(chain.input);

    const reductionGain = offlineAudioContext.createGain();
    reductionGain.gain.value = GAIN_REDUCTION_VALUE;
    chain.output.connect(reductionGain);
    reductionGain.connect(offlineAudioContext.destination);

    const createEffectsChainTime = dayjs().diff(createEffectsChainTimeStart);

    // we should start the source only when effects chain is ready
    source.start();

    const renderTimeStart = dayjs();
    const renderedSongBuffer = await offlineAudioContext.startRendering();
    const renderTime = dayjs().diff(renderTimeStart);

    const convertTimeStart = dayjs();
    const mp3Buffer = toMP3(renderedSongBuffer);
    const blob = new Blob(mp3Buffer, { type: 'audio/mpeg' });
    const convertTime = dayjs().diff(convertTimeStart);

    setIsRendering(false);

    const totalTime = dayjs().diff(totalTimeStart);

    return {
      blob,
      time: {
        loadBuffer: loadBufferTime,
        createEffectsChain: createEffectsChainTime,
        render: renderTime,
        convert: convertTime,
        total: totalTime,
      },
      duration: Math.round(source.buffer?.duration ?? 0),
      effects: filteredEffectsOrder,
    };
  };

  return {
    isLoading,
    isReady,
    isRendering,
    render,
  };
};

export default useRenderSong;
